import { base64ToBytes, bytesToBase64, utf8ToUint8Array } from './messengerV2Utils.js';
import { getItem, setItem } from './messengerV2Storage.js';
import {
  loadMessengerV2NotificationHistoryKey,
  persistMessengerV2NotificationHistoryKey,
} from './messengerNotificationCrypto.js';

const HISTORY_ALGORITHM = 'AES-GCM';
const HISTORY_IV_BYTES = 12;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const ensureCrypto = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (!cryptoApi?.subtle) {
    throw new Error('WebCrypto unavailable');
  }
  return cryptoApi;
};

const buildHistoryKey = (conversationId) => {
  const normalized = toStringOrNull(conversationId);
  return normalized ? `messenger-v2-history-key:${normalized}` : null;
};

const importKey = async (keyB64) => {
  const cryptoApi = ensureCrypto();
  const keyBytes = base64ToBytes(keyB64);
  if (!(keyBytes instanceof Uint8Array) || keyBytes.length === 0) {
    return null;
  }
  return cryptoApi.subtle.importKey('raw', keyBytes, HISTORY_ALGORITHM, false, [
    'encrypt',
    'decrypt',
  ]);
};

const getHistoryKeyB64 = async (conversationId) => {
  const storageKey = buildHistoryKey(conversationId);
  if (!storageKey) return null;
  const stored = await getItem(storageKey).catch(() => null);
  return (
    toStringOrNull(stored) ||
    (await loadMessengerV2NotificationHistoryKey(conversationId).catch(() => null))
  );
};

export const persistHistoryKey = async ({ conversationId, keyB64 } = {}) => {
  const storageKey = buildHistoryKey(conversationId);
  const normalizedKey = toStringOrNull(keyB64);
  if (!storageKey || !normalizedKey) {
    return null;
  }
  await Promise.all([
    setItem(storageKey, normalizedKey).catch(() => null),
    persistMessengerV2NotificationHistoryKey({
      conversationId,
      keyB64: normalizedKey,
    }).catch(() => null),
  ]);
  return normalizedKey;
};

export const encryptHistory = async ({
  conversationId,
  plaintext,
  keyB64 = null,
} = {}) => {
  const normalizedPlaintext = toStringOrNull(plaintext);
  if (!normalizedPlaintext) {
    return null;
  }
  const resolvedKeyB64 = toStringOrNull(keyB64) || (await getHistoryKeyB64(conversationId));
  if (!resolvedKeyB64) {
    return null;
  }

  const cryptoKey = await importKey(resolvedKeyB64);
  if (!cryptoKey) {
    return null;
  }

  const cryptoApi = ensureCrypto();
  const iv = new Uint8Array(HISTORY_IV_BYTES);
  cryptoApi.getRandomValues(iv);
  const ciphertext = await cryptoApi.subtle.encrypt(
    { name: HISTORY_ALGORITHM, iv },
    cryptoKey,
    utf8ToUint8Array(normalizedPlaintext),
  );

  return {
    history_ciphertext_b64: bytesToBase64(new Uint8Array(ciphertext)),
    history_iv_b64: bytesToBase64(iv),
    history_alg: HISTORY_ALGORITHM,
  };
};

export const decryptHistory = async ({
  conversationId,
  ciphertextB64,
  ivB64,
  keyB64 = null,
} = {}) => {
  const normalizedCiphertext = toStringOrNull(ciphertextB64);
  const normalizedIv = toStringOrNull(ivB64);
  if (!normalizedCiphertext || !normalizedIv) {
    return null;
  }

  const resolvedKeyB64 = toStringOrNull(keyB64) || (await getHistoryKeyB64(conversationId));
  if (!resolvedKeyB64) {
    return null;
  }

  const cryptoKey = await importKey(resolvedKeyB64);
  if (!cryptoKey) {
    return null;
  }

  const cryptoApi = ensureCrypto();
  const plaintext = await cryptoApi.subtle.decrypt(
    { name: HISTORY_ALGORITHM, iv: base64ToBytes(normalizedIv) },
    cryptoKey,
    base64ToBytes(normalizedCiphertext),
  );
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(new Uint8Array(plaintext));
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(new Uint8Array(plaintext)).toString('utf8');
  }
  return null;
};
